import type { AxiosResponse } from "axios"
import { isCountertop } from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import type {
	TAutoConnectEcallElevationResponse,
	TAutoConnectErefEcallsResponse,
	TDisconnectedEref,
	TEcall,
	TElevationNumber,
	TEref,
	TFurniture,
	TFurnitureCountertopSeries,
	TFurnitureDeliveryDate,
	TFurnitureOrdering,
	THighlight,
	TNote,
	TPageElevationData,
	TUpdatePageResponse,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"

/**
 * Walks each page in the cached quotation, finds an entity by id inside the
 * named array (`erefs` or `ecalls`), and replaces it via `updater`. Returns the
 * same `cached` reference when no entity matched, so TanStack's `setQueryData`
 * doesn't notify subscribers on no-op writes.
 */
const mapEntityInPages = <K extends "erefs" | "ecalls">(
	cached: AxiosResponse<TQuotationDetail> | undefined,
	field: K,
	id: number,
	updater: (entity: TPageElevationData[K][number]) => TPageElevationData[K][number] | undefined,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const list = page[field] as TPageElevationData[K]
		const idx = list.findIndex((entry) => entry.id === id)
		if (idx === -1) return page
		const nextEntry = updater(list[idx])
		if (!nextEntry) return page
		changed = true
		const nextList = list.slice() as TPageElevationData[K]
		nextList[idx] = nextEntry as TPageElevationData[K][number]
		return { ...page, [field]: nextList }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Reads the current optimistic-lock `version` of an entity from the cached quotation detail,
 * scanning every page's `furniture` / `erefs` / `ecalls`. Returns undefined when the entity
 * isn't in the cache. Used by the coordinate-edit flow to seed the mutation-queue version.
 */
export const findEntityVersion = (
	detail: TQuotationDetail | undefined,
	entityType: "furniture" | "eref" | "ecall",
	id: number,
): number | undefined => {
	const pages = detail?.related_objects
	if (!pages) return undefined
	const field = entityType === "furniture" ? "furniture" : entityType === "eref" ? "erefs" : "ecalls"
	for (const page of pages) {
		const found = (page[field] as { id: number; version: number }[]).find((entry) => entry.id === id)
		if (found) return found.version
	}
	return undefined
}

/**
 * Reads the current optimistic-lock `version` of a page-info record from the cached quotation
 * detail's `page_infos[]` (by id). Page versions live in `page_infos[]`, not `related_objects`, so
 * they need their own lookup (findEntityVersion scans related_objects only). Returns undefined when
 * the page isn't cached — used by the coordinate-edit flow to seed the mutation-queue version.
 */
export const findPageInfoVersion = (detail: TQuotationDetail | undefined, pageInfoId: number): number | undefined =>
	detail?.page_infos?.find((info) => info.id === pageInfoId)?.version

export const spliceErefIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	updated: TEref,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "erefs", updated.id, (existing) => {
		// Deep-merge estimation_config so locally-known fields (e.g. an in-flight
		// or just-persisted delivery_date) aren't wiped out when a non-config
		// PATCH (status, elevation_number, comment) returns a TEref whose
		// estimation_config is missing or partially populated.
		const mergedConfig =
			existing.estimation_config || updated.estimation_config
				? ({ ...existing.estimation_config, ...updated.estimation_config } as TEref["estimation_config"])
				: undefined
		return {
			...existing,
			...updated,
			...(mergedConfig ? { estimation_config: mergedConfig } : {}),
		}
	})

/**
 * Clears `estimation_config.delivery_date` on every cached eref that pointed at one of
 * `deletedDeliveryDateIds` — mirrors what the backend itself does: `ErefEstimationConfig.delivery_date`
 * is `on_delete=SET_NULL`, so deleting a `DeliveryDate` row nulls the FK on every referencing eref in
 * the same transaction. The quotation-configuration PATCH that deletes a delivery date never returns
 * `related_objects` (see `useUpdateQuotationConfiguration`), so nothing else re-syncs this after the
 * save — without this, an eref whose date was just deleted keeps showing the stale `{id, date,
 * project_name}` until something else happens to refetch it, and once that stale object's id no
 * longer matches any option in the (now-updated) delivery dates list, the picker has nothing to show
 * but the raw id.
 *
 * Furniture is NOT handled here — its own delivery date is resolved differently server-side (see
 * `clearDeletedDeliveryDatesFromFurniture` below) and needs to run after this one.
 */
export const clearDeletedDeliveryDatesFromErefs = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	deletedDeliveryDateIds: ReadonlySet<number>,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects || deletedDeliveryDateIds.size === 0) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		let pageChanged = false
		const nextErefs = page.erefs.map((eref) => {
			const deliveryDateId = eref.estimation_config?.delivery_date?.id
			if (deliveryDateId == null || !deletedDeliveryDateIds.has(deliveryDateId)) return eref
			pageChanged = true
			return { ...eref, estimation_config: { ...eref.estimation_config, delivery_date: null } } as TEref
		})
		if (!pageChanged) return page
		changed = true
		return { ...page, erefs: nextErefs }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Fixes up `delivery_date` on every cached furniture that pointed at one of
 * `deletedDeliveryDateIds`. Unlike an eref (see `clearDeletedDeliveryDatesFromErefs`), a furniture's
 * `delivery_date` isn't a plain FK — the backend resolves it server-side to either the furniture's own
 * override (`FurnitureProjectConfig.delivery_date`) or, absent that, the connected eref's, and tags
 * which with `is_inherited` so the UI never has to compare the two itself.
 *
 * `FurnitureProjectConfig.delivery_date` is `on_delete=CASCADE`, not `SET_NULL` (the field isn't
 * nullable — a per-object override with no date makes no sense), so deleting a `DeliveryDate` row
 * deletes the WHOLE override row for every furniture that had one, which server-side resolution then
 * answers with whatever the connected eref currently has (or nothing, if that eref has no date either,
 * or there's no connected eref). This mirrors that fallback locally, using each furniture's `eref_id` to
 * read the SAME eref state `clearDeletedDeliveryDatesFromErefs` just corrected — so this must run after
 * it, on its output, not independently.
 */
export const clearDeletedDeliveryDatesFromFurniture = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	deletedDeliveryDateIds: ReadonlySet<number>,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects || deletedDeliveryDateIds.size === 0) return cached

	const erefDeliveryDateById = new Map<number, TFurnitureDeliveryDate | null>()
	for (const page of cached.data.related_objects) {
		for (const eref of page.erefs) {
			const date = eref.estimation_config?.delivery_date
			erefDeliveryDateById.set(eref.id, date ? { ...date, project_name: date.project_name ?? null, is_inherited: true } : null)
		}
	}

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		let pageChanged = false
		const nextFurniture = page.furniture.map((item) => {
			if (item.delivery_date == null || !deletedDeliveryDateIds.has(item.delivery_date.id)) return item
			pageChanged = true
			const fallback = item.eref_id == null ? null : (erefDeliveryDateById.get(item.eref_id) ?? null)
			return { ...item, delivery_date: fallback }
		})
		if (!pageChanged) return page
		changed = true
		return { ...page, furniture: nextFurniture }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Propagates a just-changed ELEVATION-level countertop series (REQ-405) to every already-loaded
 * countertop furniture connected to that eref that is currently INHERITING (`is_inherited: true`,
 * or nothing set at all). Unlike `delivery_date`, the eref configuration PATCH's
 * `updated_furniture_list` never carries this field, so nothing else keeps an inheriting
 * countertop's displayed series in sync — this is the client-side substitute, avoiding a
 * per-furniture round trip. `series` is `null` when the elevation's own series was cleared.
 *
 * A furniture with its OWN override (`is_inherited: false`) is left untouched — same rule
 * `clearDeletedDeliveryDatesFromFurniture` follows for delivery dates. Non-countertop furniture is
 * skipped outright: it never carries this field, and `countertop_series_and_colors == null` on one
 * is indistinguishable from "inheriting nothing" without checking the category first.
 */
export const propagateInheritedCountertopSeries = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	erefId: number,
	series: Pick<TFurnitureCountertopSeries, "id" | "name"> | null,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		let pageChanged = false
		const nextFurniture = page.furniture.map((item) => {
			if (item.eref_id !== erefId || !isCountertop(item)) return item
			if (item.countertop_series_and_colors != null && !item.countertop_series_and_colors.is_inherited) return item
			pageChanged = true
			return { ...item, countertop_series_and_colors: series ? { ...series, is_inherited: true } : null }
		})
		if (!pageChanged) return page
		changed = true
		return { ...page, furniture: nextFurniture }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Adds a just-created eref (REQ-292) to the cached quotation's `related_objects[].erefs`, on the
 * page it was drawn on. Mirror of `insertFurnitureIntoCache`: an id that already exists anywhere
 * falls back to the in-place merge, so a create followed by a refetch can't produce a duplicate row.
 * Returns the same `cached` reference when the target page isn't in the cache.
 */
export const insertErefIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	created: TEref,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached

	const exists = cached.data.related_objects.some((page) => page.erefs.some((e) => e.id === created.id))
	if (exists) return spliceErefIntoCache(cached, created)

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== created.page_number) return page
		changed = true
		return { ...page, erefs: [...page.erefs, created] }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Drops a deleted eref (REQ-376) from the cached quotation's `related_objects[].erefs`.
 * Mirror of `removeFurnitureFromCache`; the furniture / ecalls the backend detached are applied
 * separately from the delete response (they are updated, not removed). Returns the same `cached`
 * reference when the eref wasn't present, so `setQueryData` doesn't notify on a no-op write.
 */
export const removeErefFromCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	erefId: number,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (!page.erefs.some((e) => e.id === erefId)) return page
		changed = true
		return { ...page, erefs: page.erefs.filter((e) => e.id !== erefId) }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Drop a deleted ecall's row from `related_objects[].ecalls` (REQ-297). Mirrors
 * `removeErefFromCache`: the erefs the ecall's elevations pointed at are only detached by the
 * backend, never deleted, so their rows stay — only their `version` moves on, which
 * `applyUpdatedErefsToCache` handles (it writes to the EREF rows, so it composes with this in
 * either order). Returns the same reference when nothing matched so React can skip the re-render.
 */
export const removeEcallFromCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (!page.ecalls.some((ecall) => ecall.id === ecallId)) return page
		changed = true
		return { ...page, ecalls: page.ecalls.filter((ecall) => ecall.id !== ecallId) }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

export const spliceEcallIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	updated: TEcall,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", updated.id, (existing) => ({ ...existing, ...updated }))

export const insertEcallIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	created: TEcall,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	const exists = cached.data.related_objects.some((page) => page.ecalls.some((ecall) => ecall.id === created.id))
	if (exists) return spliceEcallIntoCache(cached, created)

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== created.page_number) return page
		changed = true
		return { ...page, ecalls: [...page.ecalls, created] }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

export const appendEcallElevationToCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	nextEcallVersion: number,
	created: TElevationNumber,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => ({
		...existing,
		version: nextEcallVersion,
		elevation_numbers: [...existing.elevation_numbers, created],
	}))

export const replaceEcallElevationInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	updated: TElevationNumber,
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const idx = existing.elevation_numbers.findIndex((en) => en.id === updated.id)
		if (idx === -1) return existing
		const nextElevations = existing.elevation_numbers.slice()
		nextElevations[idx] = { ...nextElevations[idx], ...updated }
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: nextElevations,
		}
	})

export const replaceEcallElevationsInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	updates: TElevationNumber[],
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const byId = new Map(updates.map((u) => [u.id, u]))
		const nextElevations = existing.elevation_numbers.map((en) => {
			const patch = byId.get(en.id)
			return patch ? { ...en, ...patch } : en
		})
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: nextElevations,
		}
	})

export const upsertEcallElevationsInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	updates: TElevationNumber[],
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const byId = new Map(updates.map((u) => [u.id, u]))
		const merged = existing.elevation_numbers.map((en) => {
			const patch = byId.get(en.id)
			if (!patch) return en
			byId.delete(en.id)
			return { ...en, ...patch }
		})
		const appended = Array.from(byId.values())
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: appended.length > 0 ? [...merged, ...appended] : merged,
		}
	})

export const removeEcallElevationFromCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ecallId: number,
	elevationId: number,
	nextEcallVersion?: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "ecalls", ecallId, (existing) => {
		const idx = existing.elevation_numbers.findIndex((en) => en.id === elevationId)
		if (idx === -1) return existing
		return {
			...existing,
			version: nextEcallVersion ?? existing.version,
			elevation_numbers: existing.elevation_numbers.filter((en) => en.id !== elevationId),
		}
	})

export const applyAutoConnectErefIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	response: TAutoConnectErefEcallsResponse,
): { next: AxiosResponse<TQuotationDetail> | undefined; addedElevationIds: number[] } => {
	if (!cached?.data?.related_objects) return { next: cached, addedElevationIds: [] }
	const { eref: nextEref, connected_ecalls } = response

	const beforeIds = new Set<number>()
	cached.data.related_objects.forEach((page) =>
		page.ecalls.forEach((ecall) =>
			ecall.elevation_numbers.forEach((en) => {
				if (en.eref_id === nextEref.id) beforeIds.add(en.id)
			}),
		),
	)

	let next: AxiosResponse<TQuotationDetail> | undefined = cached
	connected_ecalls.forEach((patch) => {
		next = upsertEcallElevationsInCache(next, patch.id, patch.connected_elevations, patch.version)
	})
	next = spliceErefIntoCache(next, nextEref)

	const addedElevationIds: number[] = []
	connected_ecalls.forEach((ecall) =>
		ecall.connected_elevations.forEach((en) => {
			if (!beforeIds.has(en.id)) addedElevationIds.push(en.id)
		}),
	)

	return { next, addedElevationIds }
}

export const applyAutoConnectEcallElevationIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	response: TAutoConnectEcallElevationResponse,
): AxiosResponse<TQuotationDetail> | undefined => {
	const { ecall_elevation, ecall, eref } = response
	let next = replaceEcallElevationInCache(cached, ecall.id, ecall_elevation, ecall.version)
	next = spliceErefIntoCache(next, eref)
	return next
}

export const incrementErefVersionInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	erefId: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	mapEntityInPages(cached, "erefs", erefId, (existing) => ({ ...existing, version: existing.version + 1 }))

/**
 * Sets an eref's cached version to an explicit value (e.g. the `eref_version` returned by the
 * batch connect / disconnect endpoints). No-ops when unchanged so `setQueryData` stays quiet.
 */
export const setErefVersionInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	erefId: number,
	version: number,
): AxiosResponse<TQuotationDetail> | undefined =>
	// Return undefined (not `existing`) on a match so mapEntityInPages leaves the cache reference
	// untouched — returning the same entry still rebuilds the page/cache object.
	mapEntityInPages(cached, "erefs", erefId, (existing) =>
		existing.version === version ? undefined : { ...existing, version },
	)

/**
 * Applies the erefs a request touched (disconnected or newly connected) to the cache: their new
 * `version` and, when the response carries it, `times_referenced` (REQ-462). Forward-only — a
 * concurrent in-flight eref mutation may already have advanced the cache past this (possibly stale)
 * response, and regressing it would itself cause a conflict (the stale count is skipped with it).
 *
 * It deliberately does NOT null any ecall row's `eref_id`: the response's own rows carry the
 * authoritative links, and a sibling row linked to the same eref stays connected (#1473).
 */
export const applyUpdatedErefsToCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	updatedErefs: TDisconnectedEref[] | undefined,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!updatedErefs?.length) return cached
	let next = cached
	updatedErefs.forEach((eref) => {
		next = mapEntityInPages(next, "erefs", eref.id, (existing) => {
			if (existing.version > eref.version) return undefined
			const timesReferenced = eref.times_referenced ?? existing.times_referenced
			if (existing.version === eref.version && existing.times_referenced === timesReferenced) return undefined
			return { ...existing, version: eref.version, times_referenced: timesReferenced }
		})
	})
	return next
}

/**
 * Reconciles ecalls / ecall_elevations that the backend disconnected from an
 * eref while editing that eref (e.g. its elevation number). Each returned
 * ecall is the full post-disconnection row, so merging it replaces the cached
 * `version` and `elevation_numbers` in one shot — severing the connection
 * (`eref_id` is already nulled in the payload) and syncing versions.
 *
 * The version is only moved forward (`existing.version >= updated.version`
 * is left untouched): a concurrent in-flight ecall mutation may already have
 * advanced the cache past this (possibly stale/out-of-order) response, and
 * regressing it would itself cause a conflict — same invariant as
 * `applyUpdatedErefsToCache`.
 *
 * `disconnectedEcallElevations` is a flat fallback for elevations whose parent
 * ecall wasn't returned in `disconnectedEcalls`; elevations belonging to an
 * already-merged ecall are skipped so they can't clobber the version guard.
 */
export const applyDisconnectedEcallsToCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	disconnectedEcalls: TEcall[] | undefined,
	disconnectedEcallElevations: TElevationNumber[] | undefined,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!disconnectedEcalls?.length && !disconnectedEcallElevations?.length) return cached

	let next = cached
	const mergedEcallIds = new Set<number>()
	;(disconnectedEcalls ?? []).forEach((ecall) => {
		mergedEcallIds.add(ecall.id)
		next = mapEntityInPages(next, "ecalls", ecall.id, (existing) =>
			existing.version >= ecall.version ? existing : { ...existing, ...ecall },
		)
	})
	;(disconnectedEcallElevations ?? []).forEach((elevation) => {
		if (elevation.ecall_id == null || mergedEcallIds.has(elevation.ecall_id)) return
		next = replaceEcallElevationInCache(next, elevation.ecall_id, elevation)
	})
	return next
}

/**
 * Same pattern as `spliceErefIntoCache`, but for furniture inside
 * `related_objects[].furniture`. Used to apply mutation responses
 * (status, dimensions, config, etc.) directly to the cache without a refetch.
 */
export const spliceFurnitureIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	// Accepts a partial patch (must carry `id`) so batch responses that only return
	// `{ id, status, version }` or `{ id, version, eref_id }` can be merged too. Full
	// `TFurniture` values still satisfy this.
	updated: Partial<TFurniture> & { id: number },
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const idx = page.furniture.findIndex((f) => f.id === updated.id)
		if (idx === -1) return page
		changed = true
		const nextFurniture = page.furniture.slice()
		// Merge (don't replace) so fields the PATCH response omits — notably
		// `eref_id` — fall back to the cached value instead of becoming undefined.
		// An undefined eref_id would make the furniture vanish from the sidebars,
		// which group strictly by `eref_id === null` vs. a real eref id.
		// Mirrors `spliceErefIntoCache`.
		nextFurniture[idx] = { ...page.furniture[idx], ...updated }
		return { ...page, furniture: nextFurniture }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Removes furniture by id from every page of the cached quotation (batch-delete response).
 * Returns the same `cached` reference when nothing matched so `setQueryData` doesn't notify
 * subscribers on a no-op. Mirrors the `filter` style of `removeEcallElevationFromCache`.
 */
export const removeFurnitureFromCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ids: number[],
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects || ids.length === 0) return cached
	const idSet = new Set(ids)
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (!page.furniture.some((f) => idSet.has(f.id))) return page
		changed = true
		return { ...page, furniture: page.furniture.filter((f) => !idSet.has(f.id)) }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Inserts a brand-new furniture (e.g. the result of the duplicate endpoint) into the
 * cached quotation. Unlike `spliceFurnitureIntoCache` (update-only — it no-ops when the id
 * isn't already present), this appends the furniture to the `furniture[]` of the page that
 * matches its `page_number`, so the new highlight/sidebar entry appears without a refetch.
 *
 * Defensive guards: a furniture whose id already exists is merged in place rather than
 * duplicated (so a double-call can't add it twice), and a `page_number` with no matching
 * page leaves the cache untouched.
 */
export const insertFurnitureIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	created: TFurniture,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached

	// If the id already exists anywhere, fall back to an in-place merge (idempotent).
	const exists = cached.data.related_objects.some((page) => page.furniture.some((f) => f.id === created.id))
	if (exists) return spliceFurnitureIntoCache(cached, created)

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== created.page_number) return page
		changed = true
		return { ...page, furniture: [...page.furniture, created] }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Re-orders the furniture of a single group inside the cached quotation to match
 * the backend-provided `ordering` from a furniture PATCH response. The group is
 * either the furnitures connected to `parent_eref_id`, or — when that is `null` —
 * the no-eref furnitures on `page_number`.
 *
 * `related_objects[].furniture` is a flat per-page list; the left sidebar groups
 * it by `eref_id` and the eref sidebar filters by `eref_id`, both preserving this
 * array's order. Re-ordering the group's items into their existing slots therefore
 * fixes both views at once while leaving other groups untouched.
 */
export const reorderFurnitureInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	ordering: TFurnitureOrdering,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	const { page_number, parent_eref_id, furnitures_ids_in_order } = ordering

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== page_number) return page

		const belongsToGroup = (f: TFurniture) =>
			parent_eref_id === null ? f.eref_id === null : f.eref_id === parent_eref_id

		const groupById = new Map<number, TFurniture>()
		for (const f of page.furniture) {
			if (belongsToGroup(f)) groupById.set(f.id, f)
		}
		if (groupById.size === 0) return page

		// Build the group in the backend order, then append any group members the
		// ordering omitted (defensive) so no furniture is dropped from the cache.
		const orderedIds = new Set(furnitures_ids_in_order)
		const orderedGroup: TFurniture[] = []
		for (const id of furnitures_ids_in_order) {
			const f = groupById.get(id)
			if (f) orderedGroup.push(f)
		}
		for (const f of page.furniture) {
			if (belongsToGroup(f) && !orderedIds.has(f.id)) orderedGroup.push(f)
		}

		// Refill the group's existing slots in the flat list; other furniture stays put.
		let gi = 0
		let pageChanged = false
		const nextFurniture = page.furniture.map((f) => {
			if (!belongsToGroup(f)) return f
			const replacement = orderedGroup[gi++]
			if (replacement !== f) pageChanged = true
			return replacement
		})
		if (!pageChanged) return page

		changed = true
		return { ...page, furniture: nextFurniture }
	})

	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Adds a just-created figure highlight (REQ-400) to the cached quotation's
 * `related_objects[].highlights`, on the page it was drawn on. Mirror of `insertErefIntoCache` /
 * `insertFurnitureIntoCache`: an id that already exists anywhere is left untouched (highlights have
 * no PATCH, so there is nothing to merge) rather than duplicated. Returns the same `cached`
 * reference when the target page isn't in the cache or the id is already present.
 */
export const insertHighlightIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	created: THighlight,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached

	const exists = cached.data.related_objects.some((page) => (page.highlights ?? []).some((h) => h.id === created.id))
	if (exists) return cached

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== created.page_number) return page
		changed = true
		return { ...page, highlights: [...(page.highlights ?? []), created] }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Drops a deleted highlight (REQ-400) from the cached quotation's `related_objects[].highlights`.
 * Mirrors `removeErefFromCache` / `removeEcallFromCache`. Returns the same `cached` reference when
 * the highlight wasn't present, so `setQueryData` doesn't notify on a no-op write.
 */
export const removeHighlightFromCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	highlightId: number,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (!(page.highlights ?? []).some((h) => h.id === highlightId)) return page
		changed = true
		return { ...page, highlights: (page.highlights ?? []).filter((h) => h.id !== highlightId) }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Applies a page-name PATCH response (`PATCH …/pages/{page_info_id}/`) to the cached quotation
 * WITHOUT a refetch — the response carries everything the UI needs. Mirrors the eref-edit flow
 * (`spliceErefIntoCache` + `applyDisconnectedEcallsToCache`) plus the page-info / page-label
 * updates unique to this endpoint. Updates, all keyed unambiguously:
 *  1. `page_infos[]` (by id) — new `version` + `page_name` (+ bbox) so the next edit's optimistic
 *     lock uses the fresh version and the id→version join stays current.
 *  2. the `related_objects[]` page (by page_number) — its `page_name` (drives the left-sidebar
 *     label + eref rows, which read the page's name), its erefs' `page_name` (drives the eref
 *     detail's Page field), and its own ecalls' `location_page_name` (the callouts located on this
 *     page — drives the eref-detail callout grouping).
 *  3. merges the response `erefs` (by id) for any version bumps the backend applied.
 *  4. reconciles `disconnected_ecalls` / `disconnected_ecall_elevations` (side effects; empty for a
 *     name-only edit).
 *
 * Note: cross-page ecall references by NAME (an ecall on another page whose `page_name` points at
 * the renamed page) are not in the response and can't be resolved client-side (page names repeat),
 * so they refresh on the next natural quotation load — same limitation the backend response implies.
 */
export const applyPageUpdateToCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	response: TUpdatePageResponse,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data) return cached
	const { page_info, erefs, disconnected_ecalls, disconnected_ecall_elevations } = response

	let next: AxiosResponse<TQuotationDetail> = cached

	// 1. page_infos[] — replace the matching record (version + name + bbox).
	if (next.data.page_infos?.some((info) => info.id === page_info.id)) {
		const nextInfos = next.data.page_infos.map((info) => (info.id === page_info.id ? { ...info, ...page_info } : info))
		next = { ...next, data: { ...next.data, page_infos: nextInfos } }
	}

	// 2. related_objects[] page (by page_number) — page name + its erefs' page_name + its own
	//    ecalls' location_page_name (all this page's identity, so unambiguous by page_number).
	if (next.data.related_objects.some((page) => page.page_number === page_info.page_number)) {
		const nextPages = next.data.related_objects.map((page) => {
			if (page.page_number !== page_info.page_number) return page
			// Only rewrite the name references that still carry this page's PREVIOUS name — don't
			// blanket-stamp the new name over erefs/ecalls that pointed elsewhere (defends the
			// "everything on this page shares the page name" assumption instead of relying on it).
			const previousName = page.page_name
			return {
				...page,
				page_name: page_info.page_name,
				// TEref.page_name is a required string, so fall back to the eref's existing value on
				// the (not-expected-for-a-rename) null case.
				erefs: page.erefs.map((eref) =>
					eref.page_name === previousName && eref.page_name !== page_info.page_name
						? { ...eref, page_name: page_info.page_name ?? eref.page_name }
						: eref,
				),
				ecalls: page.ecalls.map((ecall) =>
					ecall.location_page_name === previousName && ecall.location_page_name !== page_info.page_name
						? { ...ecall, location_page_name: page_info.page_name }
						: ecall,
				),
			}
		})
		next = { ...next, data: { ...next.data, related_objects: nextPages } }
	}

	// 3. merge the response erefs (authoritative version bumps).
	for (const eref of erefs) {
		next = spliceErefIntoCache(next, eref) ?? next
	}

	// 4. reconcile disconnected ecalls / ecall_elevations (side effects).
	return applyDisconnectedEcallsToCache(next, disconnected_ecalls, disconnected_ecall_elevations) ?? next
}

// --- Notes (REQ-395/396) ---------------------------------------------------------------------
// `notes` is optional on TPageElevationData (see that type's own comment for why) — every helper
// below reads it via `?? []` and never assumes it exists.

/**
 * Reads the current optimistic-lock `version` of a note from the cached quotation detail. Every
 * note edit surface (text, position/size, font size, text color) shares ONE per-note mutation
 * queue keyed off this — mirrors `findEntityVersion` for eref/ecall/furniture.
 */
export const findNoteVersion = (detail: TQuotationDetail | undefined, noteId: number): number | undefined => {
	for (const page of detail?.related_objects ?? []) {
		const found = (page.notes ?? []).find((note) => note.id === noteId)
		if (found) return found.version
	}
	return undefined
}

/**
 * Adds a just-created note (REQ-395) to the cached quotation's `related_objects[].notes`, on the
 * page it was drawn on. Mirror of `insertErefIntoCache`: an id that already exists anywhere falls
 * back to the in-place merge, so a create followed by a refetch can't produce a duplicate row.
 * Returns the same `cached` reference when the target page isn't in the cache.
 */
export const insertNoteIntoCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	created: TNote,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached

	const exists = cached.data.related_objects.some((page) => (page.notes ?? []).some((note) => note.id === created.id))
	if (exists) return updateNoteInCache(cached, created)

	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		if (page.page_number !== created.page_number) return page
		changed = true
		return { ...page, notes: [...(page.notes ?? []), created] }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Splices a PATCHed note (REQ-396 edit — text/position/size/font size/text color) back into the
 * cache at its new version. Mirror of `spliceErefIntoCache`. Returns the same `cached` reference
 * when the note isn't present, so `setQueryData` doesn't notify on a no-op write.
 */
export const updateNoteInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	updated: TNote,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const notes = page.notes ?? []
		const idx = notes.findIndex((note) => note.id === updated.id)
		if (idx === -1) return page
		changed = true
		const nextNotes = notes.slice()
		nextNotes[idx] = updated
		return { ...page, notes: nextNotes }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

/**
 * Drops a deleted note (REQ-396) from the cached quotation's `related_objects[].notes`. Mirror of
 * `removeErefFromCache`. Returns the same `cached` reference when the note wasn't present.
 */
export const removeNoteFromCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	noteId: number,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const notes = page.notes ?? []
		if (!notes.some((note) => note.id === noteId)) return page
		changed = true
		return { ...page, notes: notes.filter((note) => note.id !== noteId) }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}
